if (!customElements.get('tabbed-content')) {
  class Tabs extends HTMLElement {
    constructor() {
      super();
      this.tabList = this.querySelector('[role="tablist"]');
      this.activeTab = this.tabList.querySelector('[aria-selected="true"]');
      this.isVerticalTablist = this.tabList.getAttribute('aria-orientation') === 'vertical';
      this.tabs = this.querySelectorAll('[role="tab"]');
      this.panels = this.querySelectorAll('[role="tabpanel"]');
      this.panelFade = this.hasAttribute('data-panel-fade');
      this.isPanelFading = false;

      // If no tab is active by default, activate the first tab.
      if (!this.activeTab) {
        this.activateTab(this.tabs[0]);
      }

      this.addListeners();
    }

    addListeners() {
      this.tabList.addEventListener('click', this.handleClick.bind(this));
      this.tabList.addEventListener('keydown', this.handleKeydown.bind(this));
    }

    /**
     * Handles 'click' events on the tablist.
     * @param {object} evt - Event object.
     */
    handleClick(evt) {
      const tab = evt.target.closest('[role="tab"]');
      if (!tab || tab === this.activeTab) return;
      this.activateTab(tab);
    }

    /**
     * Handles 'keydown' events on the tablist.
     * @param {object} evt - Event object.
     */
    handleKeydown(evt) {
      switch (evt.key) {
        case 'ArrowLeft':
        case 'ArrowRight':
          evt.preventDefault();
          if (!this.isVerticalTablist) {
            this.switchTabOnKeyPress(evt.key);
          }
          break;

        case 'ArrowUp':
        case 'ArrowDown':
          evt.preventDefault();
          if (this.isVerticalTablist) {
            this.switchTabOnKeyPress(evt.key);
          }
          break;

        // Activate the first tab.
        case 'Home':
          evt.preventDefault();
          this.activateTab(this.tabs[0]);
          break;

        // Activate the last tab.
        case 'End':
          evt.preventDefault();
          this.activateTab(this.tabs[this.tabs.length - 1]);
          break;
      }
    }

    /**
     * Activates the next or previous tab according to the key pressed.
     * @param {string} key - Key pressed.
     */
    switchTabOnKeyPress(key) {
      const tabs = [...this.tabs];
      const activeIndex = tabs.indexOf(this.activeTab);
      if (activeIndex === -1) {
        this.activateTab(tabs[0]);
        return;
      }

      if (key === 'ArrowRight' || key === 'ArrowDown') {
        // If active tab is the last tab.
        if (activeIndex === tabs.length - 1) {
          // Activate first tab.
          this.activateTab(tabs[0]);
        } else {
          // Activate next tab.
          this.activateTab(tabs[activeIndex + 1]);
        }
      } else if (key === 'ArrowLeft' || key === 'ArrowUp') {
        // If active tab is the first tab.
        if (activeIndex === 0) {
          this.activateTab(tabs[tabs.length - 1]);
        } else {
          // Activate the previous tab.
          this.activateTab(tabs[activeIndex - 1]);
        }
      }
    }

    /**
     * Activates a tab.
     * @param {Element} tab - Tab element (button).
     */
    activateTab(tab) {
      if (!tab || tab === this.activeTab || this.isPanelFading) return;

      if (this.panelFade && !Tabs.prefersReducedMotion()) {
        this.fadeToTab(tab);
        return;
      }

      this.switchToTab(tab);
    }

    /**
     * Switches to a tab without animation.
     * @param {Element} tab - Tab element (button).
     */
    switchToTab(tab) {
      if (this.activeTab) this.deactivateActiveTab();

      Tabs.setTabState(tab, true);
      tab.removeAttribute('tabindex');
      this.activeTab = tab;

      this.focusTab(tab);
    }

    /**
     * Switches to a tab with a short panel fade.
     * @param {Element} tab - Tab element (button).
     */
    fadeToTab(tab) {
      const fadeDuration = 120;
      const previousTab = this.activeTab;
      const previousPanel = Tabs.getTabPanel(previousTab);
      const nextPanel = Tabs.getTabPanel(tab);

      if (!previousPanel || !nextPanel) {
        this.switchToTab(tab);
        return;
      }

      this.isPanelFading = true;

      Tabs.setTabSelection(previousTab, false);
      previousTab.setAttribute('tabindex', '-1');

      Tabs.setTabSelection(tab, true);
      tab.removeAttribute('tabindex');
      this.activeTab = tab;
      this.focusTab(tab);

      previousPanel.classList.add('is-fading-out');

      setTimeout(() => {
        previousPanel.hidden = true;
        previousPanel.classList.remove('is-fading-out');

        nextPanel.hidden = false;
        nextPanel.classList.add('is-fading-in');

        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            nextPanel.classList.remove('is-fading-in');

            setTimeout(() => {
              this.isPanelFading = false;
            }, fadeDuration);
          });
        });
      }, fadeDuration);
    }

    /**
     * Keeps keyboard focus behavior consistent with the instant tab switch.
     * @param {Element} tab - Tab element (button).
     */
    focusTab(tab) {
      if (document.activeElement.matches('.tablist__tab')) tab.focus();
    }

    /**
     * Deactivates the currently active tab.
     */
    deactivateActiveTab() {
      if (!this.activeTab) return;

      Tabs.setTabState(this.activeTab, false);
      this.activeTab.setAttribute('tabindex', '-1');
      this.activeTab = null;
    }

    /**
     * Sets the active state of a tab and associated content panel.
     * @param {Element} tab - Tab element (button).
     * @param {boolean} active - Set the tab as active.
     */
    static setTabState(tab, active) {
      if (!tab) return;

      Tabs.setTabSelection(tab, active);

      const panel = Tabs.getTabPanel(tab);
      if (panel) panel.hidden = !active;
    }

    /**
     * Sets the active state of a tab button.
     * @param {Element} tab - Tab element (button).
     * @param {boolean} active - Set the tab as active.
     */
    static setTabSelection(tab, active) {
      if (!tab) return;

      tab.setAttribute('aria-selected', active);
    }

    /**
     * Gets the content panel associated with a tab button.
     * @param {Element} tab - Tab element (button).
     * @returns {HTMLElement}
     */
    static getTabPanel(tab) {
      if (!tab) return null;

      const panelId = tab.getAttribute('aria-controls');
      return document.getElementById(panelId);
    }

    /**
     * Checks whether the user prefers reduced motion.
     * @returns {boolean}
     */
    static prefersReducedMotion() {
      return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }
  }

  customElements.define('tabbed-content', Tabs);
}
